import type { TestSuite } from '../api'

/**
 * Strip [VERIFY: ...] tags from text so exported output is clean.
 * Tags are added by the AI when expected behavior is uncertain.
 */
export function stripVerifyTags(text: string): string {
  return text.replace(/\s*\[VERIFY:[^\]]*\]/g, '')
}

function cleanExpectedResult(text: string): string {
  return stripVerifyTags(text)
}

// Neutralize CSV formula injection: cells starting with =, +, -, @, tab, or CR
// are treated as formulas by Excel/Sheets when opened
function sanitizeCellForCSV(cell: string): string {
  const trimmed = cell.trimStart()
  if (/^[=+@\t\r]|^-(?!\d)/.test(trimmed)) {
    return "'" + cell
  }
  return cell
}

// Helper function to format CSV with proper quoting
function formatCSV(rows: string[][]): string {
  return rows
    .map((row) =>
      row
        .map((cell) => {
          const sanitized = sanitizeCellForCSV(cell)
          const escaped = sanitized.replace(/"/g, '""')
          return `"${escaped}"`
        })
        .join(',')
    )
    .join('\n')
}

// TestRail CSV format (Steps template - multi-row, one row per step)
// TestRail detects new test cases by the Title column: when Title is populated
// it starts a new test case; when Title is blank it treats the row as the next
// step of the previous test case.
export function formatAsTestRailCSV(testSuite: TestSuite, jiraTicket?: string): string {
  const headers = ['Title', 'Section', 'Type', 'Priority', 'Preconditions', 'Step', 'Expected Result', 'References']
  const rows: string[][] = [headers]

  const priorityMap: Record<string, string> = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  }

  for (const tc of testSuite.test_cases) {
    const preconditions = tc.preconditions.map((p) => `- ${p}`).join('\n')

    for (let i = 0; i < tc.steps.length; i++) {
      const step = tc.steps[i]
      const stepText = step.action + (step.test_data ? `\nTest Data: ${step.test_data}` : '')

      if (i === 0) {
        rows.push([
          tc.title,
          testSuite.test_suite_name,
          tc.type.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          priorityMap[tc.priority] || 'Medium',
          preconditions,
          stepText,
          cleanExpectedResult(step.expected_result),
          jiraTicket || '',
        ])
      } else {
        rows.push(['', '', '', '', '', stepText, cleanExpectedResult(step.expected_result), ''])
      }
    }
  }

  return formatCSV(rows)
}

// Zephyr Scale CSV format (multi-row for steps)
export function formatAsZephyrCSV(testSuite: TestSuite, jiraTicket?: string): string {
  const headers = [
    'Name',
    'Folder',
    'Priority',
    'Status',
    'Precondition',
    'Objective',
    'Test Script (Steps) - Step',
    'Test Script (Steps) - Expected Result',
    'Labels',
    'Issue Links',
  ]
  const rows: string[][] = [headers]

  const priorityMap: Record<string, string> = {
    high: 'High',
    medium: 'Normal',
    low: 'Low',
  }

  for (const tc of testSuite.test_cases) {
    const precondition = tc.preconditions.map((p) => `- ${p}`).join('\n')
    const objective = `Test type: ${tc.type.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}`
    const labels = tc.tags.join(',')

    for (let i = 0; i < tc.steps.length; i++) {
      const step = tc.steps[i]
      const stepText = step.action + (step.test_data ? ` [Data: ${step.test_data}]` : '')

      if (i === 0) {
        rows.push([
          tc.title,
          testSuite.test_suite_name,
          priorityMap[tc.priority] || 'Normal',
          'Draft',
          precondition,
          objective,
          stepText,
          cleanExpectedResult(step.expected_result),
          labels,
          jiraTicket || '',
        ])
      } else {
        rows.push(['', '', '', '', '', '', stepText, cleanExpectedResult(step.expected_result), '', ''])
      }
    }
  }

  return formatCSV(rows)
}

// Jira CSV format for task import
export function formatAsJiraCSV(testSuite: TestSuite, jiraTicket?: string): string {
  const headers = ['Summary', 'Description', 'Issue Type', 'Priority', 'Labels', 'Linked Issues']
  const rows: string[][] = [headers]

  const priorityMap: Record<string, string> = {
    high: 'Highest',
    medium: 'Medium',
    low: 'Low',
  }

  for (const tc of testSuite.test_cases) {
    const descParts: string[] = []

    if (jiraTicket) {
      descParts.push('h3. Source Requirement')
      descParts.push(`Related to: ${jiraTicket}`)
      descParts.push('')
    }

    if (tc.preconditions.length > 0) {
      descParts.push('h3. Preconditions')
      tc.preconditions.forEach((p) => descParts.push(`* ${p}`))
      descParts.push('')
    }

    descParts.push('h3. Test Steps')
    descParts.push('||Step||Action||Test Data||Expected Result||')
    for (const step of tc.steps) {
      descParts.push(`|${step.step_number}|${step.action}|${step.test_data || '-'}|${cleanExpectedResult(step.expected_result)}|`)
    }

    const description = descParts.join('\n')
    const labels = tc.tags.join(' ')

    rows.push([`[TEST] ${tc.title}`, description, 'Task', priorityMap[tc.priority] || 'Medium', labels, jiraTicket || ''])
  }

  return formatCSV(rows)
}

// Markdown format
export function formatAsMarkdown(testSuite: TestSuite): string {
  const lines: string[] = []

  lines.push(`# ${testSuite.test_suite_name}`)
  lines.push('')

  if (testSuite.requirement) {
    lines.push('## Requirement')
    lines.push(`> ${testSuite.requirement}`)
    lines.push('')
  }

  if (testSuite.generated_at) {
    lines.push(`*Generated: ${testSuite.generated_at}*`)
    lines.push('')
  }

  lines.push('---')
  lines.push('')

  for (const tc of testSuite.test_cases) {
    const typeEmoji: Record<string, string> = {
      happy_path: '',
      sad_path: '',
      edge_case: '',
    }
    const emoji = typeEmoji[tc.type] || ''

    lines.push(`## ${tc.id}: ${tc.title} ${emoji}`)
    lines.push('')

    lines.push(
      `**Type:** ${tc.type.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())} | **Priority:** ${tc.priority.charAt(0).toUpperCase() + tc.priority.slice(1)}`
    )
    if (tc.tags.length > 0) {
      lines.push(`**Tags:** ${tc.tags.join(', ')}`)
    }
    lines.push('')

    if (tc.preconditions.length > 0) {
      lines.push('### Preconditions')
      tc.preconditions.forEach((p) => lines.push(`- ${p}`))
      lines.push('')
    }

    lines.push('### Test Steps')
    lines.push('')
    lines.push('| Step | Action | Test Data | Expected Result |')
    lines.push('|------|--------|-----------|-----------------|')
    for (const step of tc.steps) {
      const action = step.action.replace(/\|/g, '\\|')
      const data = (step.test_data || '-').replace(/\|/g, '\\|')
      const expected = cleanExpectedResult(step.expected_result).replace(/\|/g, '\\|')
      lines.push(`| ${step.step_number} | ${action} | ${data} | ${expected} |`)
    }
    lines.push('')
    lines.push('---')
    lines.push('')
  }

  return lines.join('\n')
}

export type ExportFormat = 'testrail' | 'zephyr' | 'jira' | 'markdown' | 'json'

export function exportTestSuite(testSuite: TestSuite, format: ExportFormat, jiraTicket?: string): string {
  switch (format) {
    case 'testrail':
      return formatAsTestRailCSV(testSuite, jiraTicket)
    case 'zephyr':
      return formatAsZephyrCSV(testSuite, jiraTicket)
    case 'jira':
      return formatAsJiraCSV(testSuite, jiraTicket)
    case 'markdown':
      return formatAsMarkdown(testSuite)
    case 'json': {
      // Strip [VERIFY] tags from JSON export too
      const cleaned = {
        ...testSuite,
        test_cases: testSuite.test_cases.map((tc) => ({
          ...tc,
          steps: tc.steps.map((s) => ({
            ...s,
            expected_result: cleanExpectedResult(s.expected_result),
          })),
        })),
      }
      return JSON.stringify(cleaned, null, 2)
    }
    default:
      throw new Error(`Unsupported export format: ${format}`)
  }
}
